<script>
/**
 * DispatchAction -- the canonical, reusable trigger for an outbound action
 * (ship via BobGo, push an invoice to Sage, and every future dispatch).
 *
 * It is HEADLESS of business logic and of server state: the parent supplies an
 * `action` Function returning a Promise (typically `() => shipViaBobGo(id)`
 * wired to a Promise-wrapped `orders` Vuex action), and the parent derives the
 * RESULT (refs/artifacts/`alreadyComplete`) from `selected_order_details` and
 * passes it back in via props. The component owns ONLY the transient UX state
 * machine; the store owns server state (project-context: "component owns UX
 * state, store owns server state"). It never calls `window.axios` and never
 * writes to Vuex.
 *
 * Lifecycle (the contract Stories 7.3-7.8 are built against):
 *   idle --(confirm)--> in-progress --> success | failure | timeout
 * Idempotency is surfaced AS STATE: when `alreadyComplete` is true the result
 * badge (StatusPill + artifact links) is the default visible state and the
 * action button is never rendered -- "already done" is a load-time fact, not a
 * post-click discovery (UX-DR3).
 *
 * Outcome categorisation (AR-CONTRACT) -- the `action()` Promise resolves to
 * exactly one of three branches:
 *   - resolve            -> success: emit `done`; the parent's reactive props
 *                           (now reflecting the committed Order) flip us to the
 *                           badge branch.
 *   - reject {message,
 *     retryable}         -> failure: show `message` verbatim + Retry.
 *   - client timeout     -> timeout: "don't re-send yet" + Check status. Armed
 *     fires first           locally from `timeoutMs`; the real lookup-before-
 *                           create safety is server-side (later stories).
 *
 * Events: `busy-change` (Boolean) on every in-progress transition so a parent
 * panel can lock sibling dispatch controls (Story 7.8 puts Ship + Invoice side
 * by side); `done` once on a successful push.
 *
 * Reuses StatusPill (the only state renderer) and ConfirmDialog (the only
 * confirm guard) from Epic 1 -- never hand-rolls a pill or a modal.
 */
import StatusPill from "@/components/shared/StatusPill.vue";
import ConfirmDialog from "@/components/shared/ConfirmDialog.vue";

// Sentinel the client timeout rejects with, so the catch can tell a timeout
// apart from a real server failure. A plain object (not an Error) on purpose.
const TIMEOUT_SENTINEL = { __timeout: true };

export default {
  name: "DispatchAction",
  components: { StatusPill, ConfirmDialog },
  props: {
    actionLabel: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      required: true
    },
    resultBadge: {
      type: Object,
      default: () => ({ status: "done", icon: "", label: "" })
    },
    artifacts: {
      type: Array,
      default: () => []
    },
    alreadyComplete: {
      type: Boolean,
      default: false
    },
    timeoutMs: {
      type: Number,
      default: 30000,
      // A non-positive timeout would fire on the next tick and race-lose against
      // any real action(), so every dispatch would report a false timeout.
      validator: value => value > 0
    },
    action: {
      type: Function,
      required: true
    },
    // Unique per instance so two DispatchActions on one panel (Story 7.8:
    // Ship + Invoice) don't share a modal id and open each other's dialog.
    dialogId: {
      type: String,
      default: "dispatch-confirm"
    }
  },
  data() {
    return {
      // Transient UX state ONLY. Server data (refs/artifacts/alreadyComplete)
      // arrives via props from the store -- never stored here.
      phase: "idle", // 'idle' | 'in-progress' | 'failure' | 'timeout'
      errorMessage: "",
      retryable: true,
      timeoutId: null
    };
  },
  computed: {
    // Single persistent aria-live channel. A persistent region announces phase
    // transitions far more reliably than freshly-inserting a live region per
    // state, and keeping it the ONLY live region avoids double-speak with the
    // visible text (UX-DR16 / AC-6).
    liveMessage() {
      if (this.phase === "in-progress") {
        return `${this.actionLabel} in progress`;
      }
      if (this.phase === "failure") {
        return this.errorMessage;
      }
      if (this.phase === "timeout") {
        return this.timeoutCopy;
      }
      return "";
    },
    timeoutCopy() {
      return "Took too long — we'll check if it went through. Don't re-send yet.";
    }
  },
  beforeDestroy() {
    // Don't let a late timer flip state on an unmounted component.
    this.clearTimer();
    // If we're torn down mid-flight, release any sibling lock the parent armed
    // so it can't stay stuck "busy" after we're gone.
    if (this.phase === "in-progress") {
      this.$emit("busy-change", false);
    }
  },
  methods: {
    // Single execution path shared by Confirm, Retry and Check status. Check
    // status re-invokes `action()` deliberately: the contract guarantees the
    // dispatch is idempotent (lookup-before-create server-side), so a re-invoke
    // is a safe status probe, not a second send.
    run() {
      // Double-submit guard: ignore re-entry while a push is in flight
      // (ConfirmDialog guards its own button, but Retry/Check status need it).
      if (this.phase === "in-progress") {
        return;
      }

      this.phase = "in-progress";
      this.errorMessage = "";
      this.$emit("busy-change", true);

      // Promise.resolve().then() so a synchronous throw from action() is routed
      // through the same reject path rather than escaping the race.
      Promise.race([
        Promise.resolve().then(() => this.action()),
        this.timeoutPromise()
      ])
        .then(() => {
          this.phase = "idle";
          // Parent has committed the updated Order; its reactive props now flip
          // us to the badge branch. Success copy/toast is the parent's concern.
          this.$emit("done");
        })
        .catch(err => {
          if (err && err.__timeout) {
            this.phase = "timeout";
          } else {
            this.phase = "failure";
            this.errorMessage = (err && err.message) || "Dispatch failed.";
            this.retryable = !(err && err.retryable === false);
          }
        })
        .finally(() => {
          // Clear the armed timer and unlock siblings on EVERY exit path.
          this.clearTimer();
          this.$emit("busy-change", false);
        });
    },
    timeoutPromise() {
      // Fall back to the default if a caller passes a non-positive timeout
      // (the validator only warns in dev and is stripped in production).
      const ms = this.timeoutMs > 0 ? this.timeoutMs : 30000;
      return new Promise((resolve, reject) => {
        this.timeoutId = setTimeout(() => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(TIMEOUT_SENTINEL);
        }, ms);
      });
    },
    clearTimer() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }
  }
};
</script>

<template>
  <div class="dispatch-action">
    <!-- Single persistent live region -- the only a11y announcement channel. -->
    <span class="sr-only" aria-live="polite">{{ liveMessage }}</span>

    <!-- Idempotency-as-state (AC-2): badge + artifact links only, never the
         button, whenever the action is already complete. -->
    <StatusPill
      v-if="alreadyComplete"
      :status="resultBadge.status"
      :icon="resultBadge.icon"
      :label="resultBadge.label"
    >
      <a
        v-for="(artifact, index) in artifacts"
        :key="index"
        :href="artifact.href"
        class="dispatch-action__artifact"
        target="_blank"
        rel="noopener"
      >{{ artifact.label }}</a>
    </StatusPill>

    <!-- Live action surface: idle / in-progress / failure / timeout. -->
    <template v-else>
      <b-button
        v-if="phase === 'idle'"
        v-b-modal="dialogId"
        variant="primary"
        class="dispatch-action__btn"
      >{{ actionLabel }}</b-button>

      <b-button
        v-else-if="phase === 'in-progress'"
        variant="primary"
        class="dispatch-action__btn"
        disabled
        aria-busy="true"
      >
        <b-spinner small class="dispatch-action__spinner" />
        {{ actionLabel }}…
      </b-button>

      <div v-else-if="phase === 'failure'" class="dispatch-action__outcome">
        <p class="dispatch-action__message">{{ errorMessage }}</p>
        <b-button
          v-if="retryable"
          variant="primary"
          class="dispatch-action__btn"
          @click="run"
        >Retry</b-button>
      </div>

      <div v-else-if="phase === 'timeout'" class="dispatch-action__outcome">
        <p class="dispatch-action__message">{{ timeoutCopy }}</p>
        <b-button
          variant="primary"
          class="dispatch-action__btn"
          @click="run"
        >Check status</b-button>
      </div>

      <!-- The single supported open path (v-b-modal) so focus returns to the
           trigger on close. Reused, never hand-rolled. -->
      <ConfirmDialog
        :id="dialogId"
        :title="confirmText"
        :confirm-label="actionLabel"
        @confirm="run"
      />
    </template>
  </div>
</template>

<style scoped>
.dispatch-action {
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-2);
}

.dispatch-action__outcome {
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-2);
}

.dispatch-action__message {
  margin: 0;
  font-size: var(--t-small);
  color: var(--c-text);
}

/* Space between the spinner and its label without a Bootstrap util class. */
.dispatch-action__spinner {
  margin-right: var(--space-1);
}

/* Trailing artifact links inside the StatusPill slot. */
.dispatch-action__artifact {
  margin-left: var(--space-2);
}

/* Hit targets >=40px for tablet tapping (UX-DR16) -- mirrors ConfirmDialog. */
.dispatch-action__btn {
  min-height: 40px;
}

/* Visible 2px primary focus ring -- shared --focus-ring token (Story 1.6). */
.dispatch-action__btn:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}
</style>
